import { apiClient } from '../api/apiClient';
import { API_ENDPOINTS } from '../api/apiEndpoints';
import type { ApiError } from '../api/apiError';
import {
  PERMISSIONS,
  WORKFLOW_INBOX_PERMISSIONS,
} from '../constants/permissions';
import { getAttendanceHistory, getMyWeeklyAttendance } from './attendanceService';
import { getUpcomingHolidays } from './officeCalendarService';
import type {
  DashboardData,
  DashboardSectionError,
  LeaveBalanceSummary,
  MonthOverviewSummary,
  NotificationSummary,
  PendingApprovalSummary,
  TodayAttendance,
  UpcomingHolidaySummary,
  WeeklyAttendanceSummarySection,
} from '../types/dashboard';
import { hasAnyPermission, hasPermission } from '../utils/permissions';
import { formatDisplayLabel } from '../utils/camelCaseLabel';
import { calendarDateFromApi } from '../utils/dateTime';
import { countMonthOverview } from '../utils/attendanceCalendar';
import { createDefaultAttendanceDateRange } from '../utils/attendanceFilters';
import { getIsoWeekBounds } from '../utils/weeklyAttendanceReport';
import { computeSignedLeaveAvailable } from '../utils/leaveBalance';
import { parseMonthlyLopSummary } from '../utils/lopSummary';
import {
  breakStateFromEvents,
  parseDurationToMinutes,
} from '../utils/checkoutBreak';
import { holidayDateFromApi } from '../utils/officeCalendar';
import { parseAttendanceDayStatus } from '../utils/attendanceDayStatus';

type Envelope<T> = { success: boolean; message: string; data: T };
type ObjectValue = Record<string, unknown>;

export const APPROVAL_PERMISSIONS = WORKFLOW_INBOX_PERMISSIONS;

function numberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function textOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

function durationMinutesOrNull(value: unknown): number | null {
  const numeric = numberOrNull(value);
  if (numeric !== null) return numeric;
  if (typeof value !== 'string' || !value.trim()) return null;
  const clock = value.trim().match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (clock) {
    return Number(clock[1]) * 60 + Number(clock[2]);
  }
  return parseDurationToMinutes(value);
}

function firstDurationMinutes(...values: unknown[]): number | null {
  let zero: number | null = null;
  for (const value of values) {
    const minutes = durationMinutesOrNull(value);
    if (minutes === null) continue;
    if (minutes > 0) return minutes;
    zero = 0;
  }
  return zero;
}

function sectionError(error: unknown): DashboardSectionError {
  const apiError = error as ApiError;
  const messages: Record<string, string> = {
    NETWORK_ERROR: 'Unable to connect to the server.',
    REQUEST_TIMEOUT: 'The server took too long to respond.',
    ACCESS_DENIED: 'This information is not available for your account.',
  };
  return {
    message: messages[apiError.code ?? ''] ?? 'This information is temporarily unavailable.',
    code: apiError.code,
    requestId: apiError.requestId,
  };
}

export async function getTodayAttendance(): Promise<TodayAttendance> {
  const response = await apiClient.get<Envelope<ObjectValue>>(
    API_ENDPOINTS.ATTENDANCE.TODAY,
  );
  const data = response.data.data ?? {};
  const attendance = (data.attendance as ObjectValue | null) ?? {};
  const shift = (data.shift as ObjectValue | null) ?? {};
  const checkedIn = data.isCheckedIn === true;
  const checkedOut = data.isCheckedOut === true;
  const isOnBreak = data.isOnBreak === true;
  const backendStatus = textOrNull(data.status ?? attendance.statusCode);
  const events = Array.isArray(data.events)
    ? data.events.map(row => ({
        eventType: textOrNull((row as ObjectValue).eventType),
        localTimestamp: textOrNull(
          (row as ObjectValue).eventLocalTimestamp ??
            (row as ObjectValue).localTimestamp,
        ),
        timestamp: textOrNull(
          (row as ObjectValue).eventTimestampUtc ??
            (row as ObjectValue).eventTimestamp,
        ),
      }))
    : [];
  const breakState = breakStateFromEvents(events);
  const persistedBreakMinutes = firstDurationMinutes(
    attendance.breakMinutes,
    attendance.lunchBreakMinutes,
    attendance.totalBreakMinutes,
    data.breakMinutes,
    data.lunchBreakMinutes,
    data.totalBreakMinutes,
  );
  const breakMinutes =
    persistedBreakMinutes != null && persistedBreakMinutes > 0
      ? persistedBreakMinutes
      : (breakState.completedBreakMinutes ?? persistedBreakMinutes);

  return {
    attendanceId: numberOrNull(attendance.dailyAttendanceId),
    attendanceDate: calendarDateFromApi(data.attendanceDate),
    currentStatus: isOnBreak
      ? 'ON_BREAK'
      : checkedIn
        ? 'CHECKED_IN'
        : checkedOut
          ? 'CHECKED_OUT'
          : backendStatus ?? 'NOT_STARTED',
    dayStatus: parseAttendanceDayStatus(data.dayStatus),
    checkInTime: textOrNull(data.firstCheckInAt),
    checkOutTime: textOrNull(data.lastCheckOutAt),
    breakMinutes,
    workedMinutes: numberOrNull(
      attendance.netWorkMinutes ??
        attendance.workedMinutes ??
        attendance.netWorkedMinutes ??
        attendance.totalWorkedMinutes,
    ),
    expectedWorkMinutes: numberOrNull(
      shift.expectedWorkMinutes ?? attendance.expectedWorkMinutes,
    ),
    spreadMinutes: numberOrNull(
      shift.spreadMinutes ?? attendance.spreadMinutes,
    ),
    isCheckedOut: checkedOut,
    totalWorkMinutes: checkedOut
      ? numberOrNull(attendance.totalWorkMinutes)
      : null,
    minimumHalfDayMinutes: numberOrNull(
      shift.minimumHalfDayMinutes ?? attendance.minimumHalfDayMinutes,
    ),
    isHalfDay:
      data.isHalfDay === true ||
      attendance.isHalfDay === true ||
      backendStatus === 'HALF_DAY',
    hasCompletedBreak:
      breakState.hasCompletedBreak ||
      (typeof breakMinutes === 'number' && breakMinutes > 0),
    openBreakStartedAt: breakState.openBreakStartedAt,
    shiftName: textOrNull(shift.shiftName ?? attendance.shiftName),
    attendanceMode: textOrNull(
      data.attendanceSource ??
        attendance.attendanceMode ??
        attendance.attendance_mode ??
        data.attendanceMode,
    ),
    canCheckIn: data.canCheckIn === true,
    canCheckOut: data.canCheckOut === true,
    isOnBreak,
    canStartBreak: data.canStartBreak === true,
    canEndBreak: data.canEndBreak === true,
  };
}

export async function getPendingApprovalSummary(
  permissions: readonly string[],
): Promise<PendingApprovalSummary> {
  if (!hasAnyPermission(permissions, APPROVAL_PERMISSIONS)) {
    return { visible: false, count: null };
  }
  const response = await apiClient.get<
    Envelope<{ pagination?: { totalRecords?: unknown } }>
  >(API_ENDPOINTS.WORKFLOW.PENDING_APPROVALS, {
    params: { page: 1, pageSize: 1 },
  });
  return {
    visible: true,
    count: numberOrNull(response.data.data?.pagination?.totalRecords),
  };
}

export async function getLeaveBalanceSummary(
  permissions: readonly string[],
): Promise<LeaveBalanceSummary> {
  if (!hasPermission(permissions, PERMISSIONS.LEAVE_APPLY)) {
    return { visible: false, balances: [], lop: null };
  }
  const year = new Date().getFullYear();
  const response = await apiClient.get<
    Envelope<{ balances?: ObjectValue[]; lopSummary?: unknown }>
  >(API_ENDPOINTS.LEAVE.DASHBOARD, { params: { year } });
  const balances = Array.isArray(response.data.data?.balances)
    ? response.data.data.balances
    : [];
  return {
    visible: true,
    lop: parseMonthlyLopSummary(response.data.data?.lopSummary),
    balances: balances.slice(0, 3).map((item, index) => {
      const carryForward = item.carryForwardAllowed ?? item.carry_forward_allowed;
      const monthAvailable = numberOrNull(
        item.monthAvailableBalance ?? item.month_available_balance,
      );
      const withoutCarryForward =
        carryForward === false || carryForward === 0 || carryForward === '0';
      return {
        id: String(item.leaveTypeId ?? item.leaveBalanceId ?? index),
        name: formatDisplayLabel(
          textOrNull(item.leaveTypeName ?? item.leaveName ?? item.leaveTypeCode),
          'Leave balance',
        ),
        available:
          withoutCarryForward && monthAvailable !== null
            ? monthAvailable
            : computeSignedLeaveAvailable({
                openingBalance: item.openingBalance,
                creditedDays: item.creditedDays ?? item.accrued ?? item.accruedDays,
                adjustedDays: item.adjustedDays,
                debitedDays: item.debitedDays ?? item.used ?? item.usedDays,
                pendingLeaveDays:
                  item.pendingLeaveDays ?? item.pending ?? item.pendingDays,
                availableBalance:
                  item.availableBalance ??
                  item.availableDays ??
                  item.balanceDays ??
                  item.balance,
              }) ?? 0,
      };
    }),
  };
}

export async function getUnreadNotificationSummary(): Promise<NotificationSummary> {
  const response = await apiClient.get<Envelope<{ unreadCount?: unknown }>>(
    API_ENDPOINTS.NOTIFICATIONS.UNREAD_COUNT,
  );
  return {
    visible: true,
    unreadCount: numberOrNull(response.data.data?.unreadCount),
  };
}

const MONTH_OVERVIEW_PAGE_SIZE = 100;

function emptyMonthOverview(visible: boolean): MonthOverviewSummary {
  return {
    visible,
    present: null,
    absent: null,
    leave: null,
    wfh: null,
  };
}

export async function getMonthOverviewSummary(
  permissions: readonly string[],
): Promise<MonthOverviewSummary> {
  if (!hasPermission(permissions, PERMISSIONS.ATTENDANCE_VIEW)) {
    return emptyMonthOverview(false);
  }

  const filters = createDefaultAttendanceDateRange();
  const first = await getAttendanceHistory({
    filters,
    page: 1,
    pageSize: MONTH_OVERVIEW_PAGE_SIZE,
  });
  const items = [...first.items];
  for (let page = 2; page <= first.totalPages; page += 1) {
    const next = await getAttendanceHistory({
      filters,
      page,
      pageSize: MONTH_OVERVIEW_PAGE_SIZE,
    });
    items.push(...next.items);
  }

  return {
    visible: true,
    ...countMonthOverview(items),
  };
}

function emptyWeeklyAttendance(visible: boolean): WeeklyAttendanceSummarySection {
  return { visible, report: null };
}

export async function getWeeklyAttendanceSummary(
  permissions: readonly string[],
): Promise<WeeklyAttendanceSummarySection> {
  if (!hasPermission(permissions, PERMISSIONS.ATTENDANCE_VIEW)) {
    return emptyWeeklyAttendance(false);
  }

  const week = getIsoWeekBounds();
  const result = await getMyWeeklyAttendance(week);
  return {
    visible: true,
    report: result.items[0] ?? null,
  };
}

export async function getUpcomingHolidaySummary(): Promise<UpcomingHolidaySummary> {
  const holidays = await getUpcomingHolidays();
  return {
    visible: true,
    holidays: holidays.map((holiday, index) => ({
      id: String(holiday.holidayId || holiday.holidayDate || index),
      holidayDate: holidayDateFromApi(holiday.holidayDate),
      holidayName: holiday.holidayName,
      dayName: holiday.dayName ?? null,
      displayDate: holiday.displayDate ?? null,
    })),
  };
}

export async function getDashboardData(
  permissions: readonly string[],
): Promise<DashboardData> {
  const attendanceVisible = hasPermission(
    permissions,
    PERMISSIONS.ATTENDANCE_VIEW,
  );
  const requests = {
    attendance: attendanceVisible
      ? getTodayAttendance()
      : Promise.resolve<TodayAttendance | null>(null),
    approvals: getPendingApprovalSummary(permissions),
    leaveBalance: getLeaveBalanceSummary(permissions),
    notifications: getUnreadNotificationSummary(),
    monthOverview: getMonthOverviewSummary(permissions),
    weeklyAttendance: getWeeklyAttendanceSummary(permissions),
    upcomingHolidays: getUpcomingHolidaySummary(),
  };
  const keys = Object.keys(requests) as (keyof typeof requests)[];
  const settled = await Promise.allSettled(keys.map(key => requests[key]));
  const errors: DashboardData['errors'] = {};
  const values: Partial<DashboardData> = {};

  settled.forEach((result, index) => {
    const key = keys[index];
    if (result.status === 'fulfilled') values[key] = result.value as never;
    else errors[key] = sectionError(result.reason);
  });

  return {
    attendance: (values.attendance as TodayAttendance | null) ?? null,
    approvals:
      (values.approvals as PendingApprovalSummary) ?? {
        visible: hasAnyPermission(permissions, APPROVAL_PERMISSIONS),
        count: null,
      },
    leaveBalance:
      (values.leaveBalance as LeaveBalanceSummary) ?? {
        visible: hasPermission(permissions, PERMISSIONS.LEAVE_APPLY),
        balances: [],
        lop: null,
      },
    notifications:
      (values.notifications as NotificationSummary) ?? {
        visible: true,
        unreadCount: null,
      },
    monthOverview:
      (values.monthOverview as MonthOverviewSummary) ??
      emptyMonthOverview(attendanceVisible),
    weeklyAttendance:
      (values.weeklyAttendance as WeeklyAttendanceSummarySection) ??
      emptyWeeklyAttendance(attendanceVisible),
    upcomingHolidays:
      (values.upcomingHolidays as UpcomingHolidaySummary) ?? {
        visible: true,
        holidays: [],
      },
    errors,
  };
}

export { sectionError as normalizeDashboardSectionError };
